import React from 'react';
import { LotCalcule } from '../types';
import GanttBar from './GanttBar';

interface GanttChartProps {
  lots: LotCalcule[];
  projetDateDebut: string;
  projetDateFin: string;
}

const GanttChart: React.FC<GanttChartProps> = ({ lots, projetDateDebut, projetDateFin }) => {
  const projetDebut = new Date(projetDateDebut);
  const projetFin = new Date(projetDateFin);

  // Générer les marqueurs temporels (mois)
  const mois: { label: string; pct: number }[] = [];
  const totalDuration = projetFin.getTime() - projetDebut.getTime();

  if (totalDuration > 0) {
    const current = new Date(projetDebut);
    current.setDate(1);
    current.setMonth(current.getMonth() + 1);

    while (current < projetFin) {
      const pct = ((current.getTime() - projetDebut.getTime()) / totalDuration) * 100;
      const label = current.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
      mois.push({ label, pct });
      current.setMonth(current.getMonth() + 1);
    }
  }

  return (
    <section className="p-6 bg-white shadow rounded-xl mb-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Planning Gantt</h2>

      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Timeline header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-48 flex-shrink-0" />
            <div className="flex-1 relative h-6">
              {mois.map((m, i) => (
                <div
                  key={i}
                  className="absolute top-0 text-[10px] text-gray-400 -translate-x-1/2"
                  style={{ left: `${m.pct}%` }}
                >
                  {m.label}
                </div>
              ))}
            </div>
          </div>

          {/* Grid lines + bars */}
          <div className="relative">
            {/* Vertical grid lines */}
            <div className="absolute inset-0 flex items-center gap-3 pointer-events-none">
              <div className="w-48 flex-shrink-0" />
              <div className="flex-1 relative h-full">
                {mois.map((m, i) => (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 w-px bg-gray-100"
                    style={{ left: `${m.pct}%` }}
                  />
                ))}
              </div>
            </div>

            {/* Bars */}
            <div className="divide-y divide-gray-100">
              {lots.map(lot => (
                <GanttBar
                  key={lot.id}
                  lot={lot}
                  projetDebut={projetDebut}
                  projetFin={projetFin}
                />
              ))}
            </div>
          </div>

          {/* Légende */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-violet-500" />
              <span className="text-xs text-gray-500">En cours</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span className="text-xs text-gray-500">Terminé</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded bg-red-500"
                style={{
                  backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 4px)',
                }}
              />
              <span className="text-xs text-gray-500">En retard</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-gray-300" />
              <span className="text-xs text-gray-500">Non démarré</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-1.5 rounded-full bg-gray-300 opacity-60" />
              <span className="text-xs text-gray-500">Planifié</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GanttChart;
